import { useEffect } from 'react';

import { refreshAuthSession, shouldClearSessionOnRefreshFailure } from '@/features/auth/lib/sessionRefresh';
import { loginSuccess, logout } from '@/features/auth/store/authSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';

const refreshLeadTimeMs = 60_000;

function getRefreshDelay(expiresAtUtc: string | null) {
  if (!expiresAtUtc) {
    return null;
  }

  const expiresAt = new Date(expiresAtUtc).getTime();

  if (Number.isNaN(expiresAt)) {
    return null;
  }

  return Math.max(expiresAt - Date.now() - refreshLeadTimeMs, 0);
}

export function useAuthSessionRefresh() {
  const dispatch = useAppDispatch();
  const { expiresAtUtc, isAuthenticated, refreshToken, rememberMe } = useAppSelector(state => state.auth);

  useEffect(() => {
    if (!isAuthenticated || !refreshToken) {
      return;
    }

    let cancelled = false;

    const triggerRefresh = async () => {
      try {
        const refreshed = await refreshAuthSession(refreshToken);

        if (!cancelled) {
          dispatch(loginSuccess({ ...refreshed, rememberMe }));
        }
      } catch (error) {
        if (!cancelled && shouldClearSessionOnRefreshFailure(error)) {
          dispatch(logout());
        }
      }
    };

    const delayMs = getRefreshDelay(expiresAtUtc);
    if (delayMs === null) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void triggerRefresh();
    }, delayMs);

    const onVisibilityChange = () => {
      if (!cancelled && document.visibilityState === 'visible') {
        const visibleDelayMs = getRefreshDelay(expiresAtUtc);
        if (visibleDelayMs !== null && visibleDelayMs === 0) {
          void triggerRefresh();
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;

      window.clearTimeout(timeoutId);

      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch, expiresAtUtc, isAuthenticated, refreshToken, rememberMe]);
}
